/**
 * useBlockHost — compose a single-block runtime outside AppV2.
 *
 * Lets shell-level components (BottomBar, TopBar, NavPanel, …) render a cogo
 * JSON block (`@cogo/schema/blocks-v2/studio/<name>/*`) with full flow
 * execution, without paying the AppV2 tax (global theme class on <html>/<body>,
 * document keydown listener, full manifest machinery).
 *
 * Approach: compose StateManager + ActionFlowEngine + EventDispatcherV2 +
 * StoreActionRegistry directly. Uses only public exports from @cogo/runtime-v2
 * and @cogo/react-renderer-v2 — does NOT modify those packages. See
 * docs/studio/README.md §5+§9 for the runtime-stability constraint.
 *
 * State model:
 *   - block.state.localState → seeded as `local.{key}` paths on mount
 *     (mirrors app-runtime.ts:1455-1465)
 *   - overrides (Record<path, value>) → setState on each change (Object.is diff)
 *   - storeActions (Record<handlerName, impl>) → StoreActionRegistry.registerAll
 *
 * Event model:
 *   - block.events.bindings (v1 flat `"elementId:eventType"` keys) parsed into
 *     EventDispatcherV2.addBinding
 *   - ctx.dispatchEvent(elementId, eventType, payload) → lookup binding →
 *     actionEngine.findFlow → executeFlow
 */

import { useEffect, useMemo, useRef, useState } from "react"
import {
  ActionFlowEngine,
  EventDispatcherV2,
  StateManager,
  getSharedRegistry,
  wireHostHooks,
} from "@cogo/react-renderer-v2"
import type { HostServices } from "@cogo/react-renderer-v2"
import { StoreActionRegistry } from "@cogo/runtime-v2"
import type { StoreActionHandler } from "@cogo/runtime-v2"
import type { IslandRegistry, RenderContextValue } from "@cogo/react-renderer"
import type { BlockDef } from "@cogo/types-v2"
import { parseBindingKey } from "@cogo/types-v2"
import { admitAgentSurfaceCogoJson } from "@cogo/a2a-gateway/agent-surface-contract"
import { usePreviewTestingStore } from "@/features/preview/store/preview-testing-store"
import { useStudioStore } from "@/store/studio-store"
import {
  installActionFlowTraceCapture,
  readStudioTraceProjectBinding,
  type PreviewEventHint,
} from "@/features/preview/services/actionflow-trace"
import type { ActionFlowRendererProjection } from "@/features/preview/types"

export interface UseBlockHostOptions {
  /** Assembled BlockDef (meta / ui / events / flow / state / data). */
  block: BlockDef
  /** Symbol path → value. Pushed into StateManager on change (Object.is diffed). */
  overrides?: Record<string, unknown>
  /** Handler name → impl. Registered in StoreActionRegistry; invoked by `dispatchStoreAction` flow steps.
   *  Signature matches `StoreActionHandler`: receives optional params, returns void or Promise<void>. */
  storeActions?: Record<string, StoreActionHandler | undefined>
  /** Host-declared island registry. When provided, `type: "island"` nodes in
   *  `ui.cogo.json` resolve to mounted React components via IslandRenderer. When
   *  absent, the renderer warns and renders null (existing behavior — apps that
   *  don't use islands incur no runtime change). */
  islands?: IslandRegistry
  /** Optional host services. When provided, the host-effect actions
   *  (openModal / invokeCallback / emitEvent / mediaControl) become functional
   *  for this block via `wireHostHooks`; when omitted the engine keeps its
   *  headless no-op behavior (the existing default). See
   *  ../ide-v2/host/use-studio-host-services + docs/studio/actionflow-host-wiring.md. */
  hostServices?: HostServices
  /** Block id used to resolve block-scoped callbacks/media. Defaults to null —
   *  the CallbackRegistry/MediaRegistry single-handler fallback covers the
   *  single-block host case. */
  blockId?: string
  /** Capture preview ActionFlow traces into the Preview testing store. Defaults to true. */
  captureActionFlowTrace?: boolean
  /** Explicitly narrows an untrusted Agent Surface to admitted local-only flows,
   *  with all host/store/island hooks and Studio trace capture disabled. */
  interactionMode?: "standard" | "agent-surface-preview"
  /** Optional DOM projection collector used by the runtime trace overlay. */
  getRendererProjection?: () => ActionFlowRendererProjection[]
}

export interface BlockHostResult {
  /** RenderContext value to pass to `<RenderContext.Provider>` wrapping `<BlockV2>`. */
  ctx: RenderContextValue
  /** Exposed for tests / advanced hosts that need direct state access. */
  stateManager: StateManager
  /** Exposed for tests / advanced hosts. */
  actionEngine: ActionFlowEngine
  /** The detached admitted projection rendered in Agent Surface mode. */
  block: BlockDef
}

interface RuntimeInstance {
  stateManager: StateManager
  actionEngine: ActionFlowEngine
  storeActionRegistry: StoreActionRegistry
  eventDispatcher: EventDispatcherV2
}

function createRuntime(
  block: BlockDef,
): RuntimeInstance {
  // 1. StateManager — permissive mode (shell blocks are trusted host code,
  //    no stateContract enforcement needed at runtime).
  const contract = { localState: {}, inputSymbols: [], outputSymbols: [] }
  const stateManager = new StateManager({ state: block.state?.state ?? {} }, contract, { permissive: true })
  stateManager.registerNamespaceAlias("local")

  // 2. Seed `local.*` paths from state.json localState (parallels
  //    app-runtime.ts:1455-1465). BlockState type doesn't declare localState
  //    as a formal field, but the JSON convention carries it alongside
  //    `state`/`computed`/`context` (see variables.cogo.json).
  const blockStateRaw = block.state as Record<string, unknown>
  const localState = blockStateRaw?.localState
  if (localState && typeof localState === "object") {
    for (const [key, varDef] of Object.entries(localState as Record<string, unknown>)) {
      const def = varDef as { default?: unknown }
      if (def?.default !== undefined) {
        stateManager.setState(`local.${key}`, structuredClone(def.default))
      }
    }
  }

  // 3. ActionFlowEngine (no platform adapter — shell blocks don't need
  //    navigation/storage/etc. platform actions).
  const actionEngine = new ActionFlowEngine(stateManager)

  // 4. Register flows from block.flow.flows.
  const flowMap = (block.flow as { flows?: Record<string, unknown> })?.flows
  if (flowMap) {
    actionEngine.registerFlows(flowMap as never)
  }

  // 5. StoreActionRegistry — wired to engine so `dispatchStoreAction` steps
  //    can invoke host-provided handlers by name. Populated from `storeActions`
  //    option in a useEffect below.
  const storeActionRegistry = new StoreActionRegistry()
  actionEngine.setStoreActionRegistry(storeActionRegistry)

  // 6. EventDispatcher — parse block.events.bindings (v1 flat keys).
  const eventDispatcher = new EventDispatcherV2()
  const bindings = (block.events as { bindings?: Record<string, unknown> })?.bindings
  if (bindings && typeof bindings === "object") {
    for (const [key, binding] of Object.entries(bindings)) {
      const parsed = parseBindingKey(key)
      if (!parsed) continue
      const { elementId, eventType } = parsed
      if (typeof binding === "string") {
        eventDispatcher.addBinding(elementId, eventType, { flow: binding })
      } else if (typeof binding === "object" && binding !== null) {
        const b = binding as { flow?: string; params?: Record<string, unknown> }
        if (b.flow) {
          eventDispatcher.addBinding(elementId, eventType, { flow: b.flow, params: b.params })
        }
      }
    }
  }

  return { stateManager, actionEngine, storeActionRegistry, eventDispatcher }
}

export function useBlockHost({
  block,
  overrides,
  storeActions,
  islands,
  hostServices,
  blockId,
  captureActionFlowTrace = true,
  interactionMode = "standard",
  getRendererProjection,
}: UseBlockHostOptions): BlockHostResult {
  const [, forceUpdate] = useState(0)
  const eventHintRef = useRef<PreviewEventHint | null>(null)
  const admittedBlock = useMemo(
    () =>
      interactionMode === "agent-surface-preview"
        ? (admitAgentSurfaceCogoJson(block) as unknown as BlockDef)
        : block,
    [block, interactionMode],
  )

  // Runtime is keyed on block identity. Shell blocks import their sidecars
  // at module scope so the reference is stable; test harnesses that rebuild
  // BlockDef per render would thrash this — they should memo the BlockDef.
  //
  // Overrides are seeded HERE, as the runtime is built, not only in the effect
  // below. An override is the host's answer for a `local.*` path, and the
  // FIRST render has to read it: the dashboard passes `local.route`, whose
  // authored default is 'home', so seeding a commit late made every
  // `/studio/<screen>` deep link render Home first and then swap. That flash
  // was not just cosmetic — Home's mount effects ran, and its `/api/projects`
  // read fired on screens the user never asked for (operator measurement,
  // 2026-07-31). `overrides` is intentionally NOT a dependency: this is the
  // seed for a freshly built runtime, and the effect below owns every later
  // change.
  const runtime = useMemo(() => {
    const created = createRuntime(admittedBlock)
    if (overrides) {
      for (const [path, value] of Object.entries(overrides)) {
        created.stateManager.setState(path, value)
      }
    }
    return created
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admittedBlock])

  // Subscribe FIRST so any setState fired by downstream effects (override
  // seed, storeActions sync, flow execution) triggers a re-render. Must run
  // before the override effect — otherwise the initial override write
  // reaches the store but no one is listening, so the UI stays on defaults.
  useEffect(() => runtime.stateManager.subscribe("", () => forceUpdate((n) => n + 1)), [runtime])

  // Push overrides into StateManager on change (Object.is diffed). Seeded
  // with what the runtime was built with, so the first pass re-writes nothing.
  const prevOverridesRef = useRef<Record<string, unknown>>({ ...overrides })
  useEffect(() => {
    if (!overrides) {
      prevOverridesRef.current = {}
      return
    }
    const prev = prevOverridesRef.current
    const next: Record<string, unknown> = {}
    for (const [path, value] of Object.entries(overrides)) {
      next[path] = value
      if (!Object.is(prev[path], value)) {
        runtime.stateManager.setState(path, value)
      }
    }
    prevOverridesRef.current = next
  })

  // Sync storeActions → registry on change. `clear()` then re-register to
  // reflect additions and removals atomically.
  useEffect(() => {
    runtime.storeActionRegistry.clear()
    if (interactionMode === "standard" && storeActions) {
      runtime.storeActionRegistry.registerAll(storeActions)
    }
  }, [runtime, storeActions, interactionMode])

  // Wire the four host-effect hooks when hostServices is provided (additive —
  // absent → the engine keeps its headless no-op behavior). Block-scoped
  // callbacks/media resolve to `blockId` (null fallback covers single-block).
  useEffect(() => {
    if (interactionMode !== "standard" || !hostServices) return
    wireHostHooks(runtime.actionEngine, hostServices, () => blockId ?? null)
  }, [runtime, hostServices, blockId, interactionMode])

  useEffect(() => {
    if (interactionMode !== "standard" || !captureActionFlowTrace) return
    installActionFlowTraceCapture({
      stateManager: runtime.stateManager,
      actionEngine: runtime.actionEngine,
      getEventHint: () => eventHintRef.current,
      getRendererProjection,
      getProductionBinding: () => readStudioTraceProjectBinding(
        useStudioStore.getState(),
      ),
      onTrace: (trace) => {
        usePreviewTestingStore.getState().addActionFlowTrace(trace)
        useStudioStore.getState().applyActionFlowBrowserTraceEvidence(trace)
      },
    })
  }, [runtime, captureActionFlowTrace, getRendererProjection, interactionMode])

  // Build the RenderContextValue consumed by `<BlockV2>`.
  const ctx = useMemo<RenderContextValue>(
    () => ({
      registry: getSharedRegistry(),
      resolveSymbol: (expr) => runtime.stateManager.resolve(expr),
      dispatchEvent: (elementId, eventType, payload) => {
        const binding = runtime.eventDispatcher.getBinding(elementId, eventType)
        if (!binding?.flow) return
        const flow = runtime.actionEngine.findFlow(binding.flow)
        if (!flow) return
        const eventPayload =
          payload != null && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : { value: payload }
        const ctxArg = { ...eventPayload, event: eventPayload }
        eventHintRef.current = {
          elementId,
          eventType,
          ...(readPayloadEventValue(payload) !== undefined ? { eventValue: readPayloadEventValue(payload) } : {}),
        }
        // .catch after .finally: a rejecting step (e.g. a restApi fetch
        // failure in a legacy-aliased flow) must not surface as an unhandled
        // rejection — the trace wrapper has already captured the error into
        // the preview trace store before rethrowing.
        void runtime.actionEngine
          .executeFlow(flow, ctxArg)
          .finally(() => {
            if (
              eventHintRef.current?.elementId === elementId &&
              eventHintRef.current.eventType === eventType
            ) {
              eventHintRef.current = null
            }
          })
          .catch((err) => {
            console.error('[studio preview] flow execution failed', err)
          })
      },
      hasEventBinding: (elementId, eventType) =>
        Boolean(runtime.eventDispatcher.getBinding(elementId, eventType)),
      mode: "preview",
      blocks: {},
      islands: interactionMode === "standard" ? islands : undefined,
    }),
    [runtime, islands, interactionMode],
  )

  return {
    ctx,
    stateManager: runtime.stateManager,
    actionEngine: runtime.actionEngine,
    block: admittedBlock,
  }
}

function readPayloadEventValue(payload: unknown): unknown {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && "value" in payload) {
    return (payload as { value?: unknown }).value
  }
  return payload
}
